import {UserLearnerStatisticInterface} from './learner-statistic.model';
import {TopStatisticsInfo} from './ai-scorecard-statistic.model';

export interface BaseStatisticSummaryInterface {
  firstScore: number;
  bestScore: number;
  avgScore: number;
  attemptToMastery: number;
  timeToMastery: number;
  improvement: number;
  performanceImprovement: number;
  consistencyGap: number;
  totalAttempts: number;
  totalDuration: number;
}

export interface ManagerStatisticSummaryInterface extends BaseStatisticSummaryInterface {
  completedPercent: number;
  completed: number;
}

export interface UserStatisticSummaryInterface extends BaseStatisticSummaryInterface {
  firstScore: number;
}

export class ManagerTopStatistics implements ManagerStatisticSummaryInterface {
  public readonly statistics: TopStatisticsInfo;
  public readonly completed: number;
  public readonly completedPercent: number;
  public readonly avgScore: number;
  public readonly firstScore: number;
  public readonly bestScore: number;
  public readonly attemptToMastery: number;
  public readonly timeToMastery: number;
  public readonly improvement: number;
  public readonly performanceImprovement: number;
  public readonly consistencyGap: number;
  public readonly totalAttempts: number;
  public readonly totalDuration: number;

  constructor(statistics: TopStatisticsInfo) {
    this.statistics = statistics;
    if (this.statistics.totalScore && this.statistics.totalAttempts) {
      this.avgScore = this.statistics.totalScore / this.statistics.totalAttempts;
    } else {
      this.avgScore = 0;
    }
    this.firstScore = this.statistics.firstAttemptScore || 0;
    this.bestScore = this.statistics.bestAttemptScore || 0;

    this.improvement = this.avgScore - this.firstScore;
    this.consistencyGap = this.bestScore - this.avgScore;
    this.performanceImprovement = this.bestScore - this.firstScore;

    this.attemptToMastery = this.statistics.thresholdAttempt || 0;
    this.timeToMastery = this.statistics.thresholdDuration / 1000 / 60;
    this.totalAttempts = this.statistics.totalAttempts || 0;
    this.totalDuration = this.statistics.totalDuration / 1000 / 60;
  }
}

export class AvgUserStatistic implements UserStatisticSummaryInterface {
  public readonly data: UserLearnerStatisticInterface[];
  public readonly firstScore: number;
  public readonly bestScore: number;
  public readonly avgScore: number;
  public readonly attemptToMastery: number;
  public readonly timeToMastery: number;
  public readonly improvement: number;
  public readonly performanceImprovement: number;
  public readonly consistencyGap: number;
  public readonly totalScore: number;
  public readonly totalAttempts: number;
  public readonly totalDuration: number;

  constructor(data: UserLearnerStatisticInterface[]) {
    this.data = data;
    this.firstScore = this.calcAvg(this.data.filter(d => d.firstScore > 0).map(d => d.firstScore));
    this.bestScore = this.calcAvg(this.data.filter(d => d.bestScore > 0).map(d => d.bestScore));
    this.avgScore = this.calcAvg(this.data.filter(d => d.avgScore > 0).map(d => d.avgScore));

    this.improvement = this.avgScore - this.firstScore;
    this.performanceImprovement = this.bestScore - this.firstScore;
    this.consistencyGap = this.bestScore - this.avgScore;

    this.attemptToMastery = this.calcAvg(this.data.filter(d => d.attemptToMastery > 0).map(d => d.attemptToMastery));
    this.timeToMastery = this.calcAvg(this.data.filter(d => d.timeToMastery > 0).map(d => d.timeToMastery));
    this.totalAttempts = this.calcAvg(this.data.filter(d => d.totalAttempts > 0).map(d => d.totalAttempts));
    this.totalDuration = this.calcAvg(this.data.filter(d => d.totalDuration > 0).map(d => d.totalDuration));
  }

  private calcAvg(values: number[]): number {
    if (!values.length) {
      return 0;
    }
    return values.reduce((a, b) => a + b, 0) / values.length;
  }
}
